/**
 * Password strength scoring for the `/register` password mode meter.
 *
 * Pure, render-free logic (per the area's "extract pure logic out of components" rule) so it can
 * be unit-tested without mounting the page. Maps a raw password to one of four levels and a
 * 0..3 segment count for the three-bar meter. The minimum accepted password is 8 characters
 * (enforced separately by the submit gate); anything shorter is always `too-short` (the meter's
 * red, not-submittable state). `weak` means it meets the length minimum but is otherwise plain.
 */

export type PasswordStrengthLevel = 'too-short' | 'weak' | 'fair' | 'strong';

export type PasswordStrengthResult = {
  level: PasswordStrengthLevel;
  /** Number of meter segments to fill (0 when empty, otherwise 1..3 matching the level). */
  score: 0 | 1 | 2 | 3;
};

/**
 * Score a password into a strength level + a filled-segment count.
 *
 * Empty → `{ too-short, 0 }` (no segments, but the meter still reserves its space). Below the
 * 8-char minimum → always `{ too-short, 1 }` (red, cannot submit). At or above the minimum, points
 * are awarded for extra length and character-class variety, mapping to `weak` (1 segment, plain),
 * `fair` (2 segments) or `strong` (3 segments).
 */
export const scorePassword = (password: string): PasswordStrengthResult => {
  if (password.length === 0) {
    return { level: 'too-short', score: 0 };
  }

  if (password.length < 8) {
    return { level: 'too-short', score: 1 };
  }

  let points = 0;
  if (password.length >= 12) points += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) points += 1;
  if (/\d/.test(password)) points += 1;
  if (/[^A-Za-z0-9]/.test(password)) points += 1;

  // 8+ chars but otherwise plain (e.g. all lowercase) stays weak; some variety lifts it to fair;
  // strong needs real diversity (length and/or multiple character classes).
  if (points <= 1) {
    return { level: 'weak', score: 1 };
  }
  if (points === 2) {
    return { level: 'fair', score: 2 };
  }
  return { level: 'strong', score: 3 };
};
